<template>
  <CCard class="settings-card">
    <CCardHeader>
      <div class="section-heading">
        <span class="section-icon"><CIcon name="cil-link"/></span>
        <div>
          <strong>Google / Microsoft アカウント</strong>
          <p>
            会社のアカウントと連携すると、ボタンひとつでログインできます。
            <b>連携するまでは使えません。</b>
            メールアドレスが一致していても、自動では連携されません。
          </p>
        </div>
      </div>
    </CCardHeader>

    <CCardBody>
      <div v-if="loading" class="security-settings-loading">
        <CSpinner color="primary"/>
        <span>連携状態を読み込んでいます…</span>
      </div>

      <template v-else>
        <CAlert
          v-if="errorMessage"
          color="danger"
          close-button
          @update:show="errorMessage = ''"
        >{{ errorMessage }}</CAlert>

        <CAlert
          v-if="message"
          color="success"
          close-button
          @update:show="message = ''"
        >{{ message }}</CAlert>

        <p v-if="!identities.length" class="security-empty">
          連携している外部アカウントはありません。
        </p>

        <ul v-else class="security-session-list">
          <li
            v-for="item in identities"
            :key="item.id"
            class="security-session"
          >
            <div class="security-session__body">
              <p class="security-session__title">
                {{ describeProvider(item.provider) }}
              </p>
              <p class="security-session__detail">
                アカウント： {{ item.linkedEmail || '不明' }}
              </p>
              <p class="security-session__detail">
                連携日時： {{ formatTime(item.linkedAt) }}
                 / 最終利用： {{ formatTime(item.lastLoginAt) }}
              </p>
            </div>
            <CButton
              color="outline-danger"
              size="sm"
              :disabled="busy"
              @click="unlink(item)"
            >解除</CButton>
          </li>
        </ul>

        <!--
          使えない提供元のボタンは出さない。**押しても必ず失敗する入口を
          見せない。**開いていない理由（未設定か、そもそも使わないか）は
          利用者には区別できないので、まとめて1つの案内にする。
        -->
        <div v-if="linkable.length" class="external-link-actions">
          <CButton
            v-for="one in linkable"
            :key="one.key"
            color="outline-primary"
            :disabled="busy"
            @click="link(one)"
          >{{ one.label }} と連携する</CButton>
        </div>
        <!--
          足りていないものによって、言う相手が変わる。

          **「管理者の設定が必要です」しか出せなかった。**PREIKA側が
          未提供なだけのときも同じ文が出るので、設定済みの管理者が
          何度も自社設定を見に行くことになった。
        -->
        <p v-else-if="nothingOffered" class="security-settings-note">
          外部アカウントとの連携は、現在ご利用いただけません。
          ご利用をご検討の場合は、サポートへお問い合わせください。
        </p>
        <p v-else-if="blockedByCompany" class="security-settings-note">
          外部アカウントの連携は、現在この会社では許可されていません。
          利用するには、会社の管理者による設定が必要です。
        </p>
        <p v-else class="security-settings-note">
          連携可能な外部アカウントはありません。
        </p>
      </template>
    </CCardBody>
  </CCard>
</template>

<script>
import { startGoogleLink } from '../../services/googleLogin'
import { startMicrosoftLink } from '../../services/microsoftLogin'
import {
  fetchLinkableProviders,
  listExternalIdentities,
  unlinkExternalIdentity,
  isLastSignInMethod,
  isNetworkFailure
} from '../../services/externalIdpLogin'
import { isReauthenticationRequired } from '../../services/passkey'

/**
 * Google / Microsoft の結び付けを見る・足す・外す画面。
 *
 * <h3>メールが同じでも自動で結び付けない</h3>
 *
 * <p>「IdPが返したメールがPREIKAのメールと同じだから同じ人」という判定は、
 * <b>そのメールアドレスを一時的に持てる人が他人のPREIKAへ入れる</b>ことに
 * なる。だから本人がログイン済みの状態で明示的に結び付ける。</p>
 *
 * <h3>結び付けはこの画面を離れる</h3>
 *
 * <p>押すとIdPへ飛び、戻ってくるのはサーバが決めた行き先
 * （{@code /#/my/security?link=...}）。<b>戻ってきた印をここで読む。</b>
 * 読まないと、やった本人が成否を知る方法がない。</p>
 */

const PROVIDERS = [
  {
    key: 'google',
    label: 'Google',
    startLink: startGoogleLink
  },
  {
    key: 'microsoft',
    label: 'Microsoft',
    startLink: startMicrosoftLink
  }
]

const PROVIDER_LABELS = {
  GOOGLE: 'Google アカウント',
  MICROSOFT: 'Microsoft アカウント',
  ENTERPRISE_OIDC: '会社のログイン'
}

export default {
  name: 'ExternalAccountSettings',

  data () {
    return {
      identities: [],
      /**
       * 提供元ごとの {offered, allowedByCompany}。
       *
       * **会社の設定まで見る。**以前は PREIKA 側の可否しか見て
       * いなかったので、Google を閉じている会社の利用者にも
       * ボタンが出た（押しても通らない）。
       */
      available: {},
      loading: true,
      busy: false,
      message: '',
      errorMessage: ''
    }
  },

  computed: {
    /** まだ結び付けていなくて、かつ使える提供元だけ。 */
    linkable () {
      const linked = this.identities.map(item => String(item.provider || ''))
      return PROVIDERS.filter(one =>
        this.allowedHere(one.key) &&
        linked.indexOf(one.key.toUpperCase()) < 0)
    },

    /** PREIKA がどれも用意していない。**言う相手はサポート。** */
    nothingOffered () {
      return PROVIDERS.every(one => !this.offered(one.key))
    },

    /** 用意はあるが、会社が許していない。**言う相手は自社の管理者。** */
    blockedByCompany () {
      return PROVIDERS.some(one => this.offered(one.key))
        && PROVIDERS.every(one => !this.allowedHere(one.key))
    }
  },

  mounted () {
    this.applyReturnMark()
    this.reload()
  },

  methods: {
    /** PREIKA がその提供元を用意しているか。 */
    offered (key) {
      const one = this.available[key]
      return Boolean(one && one.offered)
    },

    /**
     * いま自分が結び付けられるか。
     *
     * **用意されていることと、会社が許していることは別。**片方だけ見ると、
     * 押しても通らないボタンが出る。
     */
    allowedHere (key) {
      const one = this.available[key]
      return Boolean(one && one.offered && one.allowedByCompany)
    },

    async reload () {
      this.loading = true

      /*
       * **1回で聞く。**提供元ごとに別々の口へ聞いていたが、あれは
       * PREIKA 側の可否しか見ていなかった。会社が閉じていてもボタンが
       * 出て、押すと断られる。
       *
       * 聞けなかったら閉じている扱い。分からないときにボタンを出すと、
       * 押しても必ず失敗する。
       */
      try {
        this.available = await fetchLinkableProviders()
      } catch (error) {
        this.available = {}
      }

      try {
        this.identities = await listExternalIdentities()
      } catch (error) {
        this.identities = []
        this.errorMessage = this.explain(error,
          '連携状態を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    /**
     * 戻ってきた印を読む。
     *
     * <p>サーバが作る印は <code>google-linked</code> /
     * <code>google-failed</code> / <code>microsoft-…</code> の4つだけ。
     * <b>知らない値は無視する。</b>URLに何を入れられても、画面に出るのは
     * こちらが決めた文言だけになる。</p>
     */
    applyReturnMark () {
      const query = (this.$route && this.$route.query) || {}
      const mark = String(query.link || '')
      if (!mark) return

      const provider = PROVIDERS.find(one => mark.indexOf(one.key + '-') === 0)
      if (provider) {
        if (mark === provider.key + '-linked') {
          this.message = provider.label + ' アカウントと連携しました。'
        } else if (mark === provider.key + '-failed') {
          this.errorMessage = provider.label
            + ' アカウントと連携できませんでした。'
            + ' 会社のアカウントでログインしているかご確認ください。'
        }
      }

      /*
       * 印はURLから消す。残すと、読み込み直すたびに同じ文言が出る。
       * ルータが無いところでも落ちないようにする。
       */
      if (this.$router && this.$route) {
        const next = Object.assign({}, this.$route.query)
        delete next.link
        this.$router.replace({ query: next }).catch(() => {})
      }
    },

    async link (provider) {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        const url = await provider.startLink()
        /*
         * ここから先はブラウザが離れる。**戻ってくるのはサーバが決めた
         * 行き先だけ。**画面がURLを組み立てない。
         */
        window.location.assign(url)
      } catch (error) {
        this.errorMessage = this.explain(error,
          provider.label + ' との連携を開始できませんでした。')
        this.busy = false
      }
    },

    async unlink (item) {
      if (this.busy) return
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        await unlinkExternalIdentity(item.id)
        this.message = describe(item.provider) + ' との連携を解除しました。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(error, '解除できませんでした。')
      } finally {
        this.busy = false
      }
    },

    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして再ログインしてから、もう一度お試しください。'
      }
      if (isLastSignInMethod(error)) {
        return this.serverText(error)
          || '有効なログイン方法がこれのみになるため、解除できません。'
          + ' 解除する前に、別のログイン方法を設定してください。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return this.serverText(error) || fallback
    },

    serverText (error) {
      const response = error && error.response
      const data = (response && response.data) || {}
      return typeof data.message === 'string' && data.message
        ? data.message
        : null
    },

    describeProvider (provider) {
      return describe(provider)
    },

    formatTime (value) {
      if (!value) return '未使用'
      const at = new Date(value)
      if (isNaN(at.getTime())) return '不明'
      return at.toLocaleString('ja-JP')
    }
  }
}

/**
 * 提供元の見せ方。
 *
 * <p><b>知らない値をそのまま出さない。</b>サーバが提供元を増やしたときに、
 * 英大文字の値が画面へ出る。</p>
 */
function describe (provider) {
  return PROVIDER_LABELS[String(provider || '')] || 'その他の連携'
}
</script>
